import { FormApi } from "final-form";

import { OutboundCommChannel } from "@libs/gateways/comms/CommsGateway.dtos.ts";
import {
  CommunicationType,
  ContactType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { CommunicationFieldValue } from "@shared-types/practice/communication-field-value.type.ts";

import {
  checkIfCanSet,
  ConsentCommsPreference,
  ConsentCommType,
  consentPreferenceFields,
  getCommTypeCount,
  getPreference,
  getSMSOverEmailPreference,
  PatientEditFormField
} from "../commPreferenceUtils.ts";
import { PatientEditFormValues } from "../PatientEditFormValues.tsx";

const setConsentPreference = (
  field: PatientEditFormField,
  form: FormApi<PatientEditFormValues>,
  fieldValueToSet?: string
) => {
  const { value, initialValue, name } = field;

  checkIfCanSet(value, initialValue, fieldValueToSet) &&
    form.change(name, fieldValueToSet);
};

export const onChangeCommunications = (
  communications: CommunicationFieldValue[],
  formOptions: {
    initialValues: Partial<PatientEditFormValues>;
    values: PatientEditFormValues;
    isHealthProvider: boolean;
  },
  form: FormApi<PatientEditFormValues>
) => {
  const { initialValues, values, isHealthProvider } = formOptions;

  const { Email, Mobile } = CommunicationType;
  const mobileCount = getCommTypeCount(communications, Mobile);
  const emailCount = getCommTypeCount(communications, Email);

  const consentPreference: ConsentCommsPreference = {
    SMSOverEmail: getSMSOverEmailPreference(
      mobileCount,
      emailCount,
      isHealthProvider
    ),
    EmailOnly: getPreference(emailCount, OutboundCommChannel.Email),
    SMSOnly: isHealthProvider
      ? { eraseValue: false }
      : getPreference(mobileCount, OutboundCommChannel.Sms)
  };

  form.batch(() => {
    Object.entries(consentPreferenceFields).forEach(preferenceField => {
      const [consentCommType, fieldNames] = preferenceField;
      fieldNames.forEach(fieldName => {
        const { typeFieldName, valueFieldName } = fieldName;

        if (
          values.type !== ContactType.Patient &&
          typeFieldName !== "invoiceCommunicationType" &&
          typeFieldName !== "formNotifyType"
        ) {
          return;
        }

        const { outboundComm, eraseValue } =
          consentPreference[consentCommType as ConsentCommType];
        setConsentPreference(
          {
            name: typeFieldName,
            value: values[typeFieldName as keyof PatientEditFormValues],
            initialValue:
              initialValues[typeFieldName as keyof PatientEditFormValues]
          } as PatientEditFormField,
          form,
          outboundComm
        );
        eraseValue &&
          initialValues[valueFieldName as keyof PatientEditFormValues] ===
            undefined &&
          form.change(valueFieldName, undefined);
      });
    });
  });
};

const MIN_MOBILE_LENGTH = 9;

export const isMobileMinLengthReached = (
  type: CommunicationType,
  value: string | undefined
) =>
  type === CommunicationType.Mobile &&
  (value?.length || 0) >= MIN_MOBILE_LENGTH;
